import { z } from 'zod';
import { TrackSchema } from './classify';

export const ApplyJobSkipReasonSchema = z.enum(['already_applied', 'already_rejected', 'stale']);

/**
 * ApplyJobSchema - shape of an apply_jobs row returned from the API
 */
export const ApplyJobSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  status: z.enum(['PENDING', 'IN_PROGRESS', 'COMPLETED', 'FAILED']),
  track: TrackSchema.nullable(),
  suggestion_ids: z.array(z.string().uuid()),
  total_suggestions: z.number().int().min(0),
  applied_count: z.number().int().min(0),
  skipped_count: z.number().int().min(0).default(0),
  skipped_suggestion_ids: z.array(z.string().uuid()).default([]),
  skip_reasons: z.record(z.string().uuid(), ApplyJobSkipReasonSchema).default({}),
  failed_count: z.number().int().min(0),
  failed_suggestion_ids: z.array(z.string().uuid()),
  error_message: z.string().nullable(),
  operation_id: z.string().nullable(),
  created_at: z.string(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
}).superRefine((job, ctx) => {
  if (
    job.status === 'COMPLETED'
    && job.applied_count + job.skipped_count + job.failed_count !== job.total_suggestions
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['total_suggestions'],
      message: 'Completed job outcome counts must equal total_suggestions',
    });
  }
});
export type ApplyJob = z.infer<typeof ApplyJobSchema>;

/**
 * POST /v1/classify/suggestions/batch-apply
 */
export const BatchApplyRequestSchema = z.object({
  track: TrackSchema.optional(),
  suggestion_ids: z.array(z.string().uuid()).optional(),
  idempotency_key: z.string().trim().min(1).max(255).optional(),
  /**
   * Filter to a single category (matches `labels[0]`) when resolving the
   * cohort server-side. Pass the `UNCATEGORIZED_LABEL` ("Uncategorized")
   * sentinel to select rows with no labels. Ignored when `suggestion_ids`
   * is provided. See issue #651.
   */
  label: z.string().trim().min(1).max(120).optional(),
  /**
   * ILIKE search across subject/from_address/snippet, applied to the
   * server-side resolution the same way `ListSuggestionsRequestSchema.search`
   * applies it to listing. Ignored when `suggestion_ids` is provided. See
   * issue #651.
   */
  search: z.string().optional(),
  /**
   * Display-scale (0.80-1.0) minimum confidence for the resolved cohort.
   * Mirrors `ListSuggestionsRequestSchema.confidenceFloor` so the apply
   * cohort matches whatever the user is currently viewing. Ignored when
   * `suggestion_ids` is provided; defaults to the shared display threshold
   * when omitted. See issue #651.
   */
  confidence_floor: z.coerce.number().min(0.80).max(1).optional(),
});
export type BatchApplyRequest = z.infer<typeof BatchApplyRequestSchema>;

export const BatchApplyResponseSchema = z.object({
  job_id: z.string().uuid(),
  total_suggestions: z.number().int().min(0),
});
export type BatchApplyResponse = z.infer<typeof BatchApplyResponseSchema>;

/**
 * GET /v1/classify/apply-jobs/active - returns the active job or null
 */
export const GetActiveApplyJobResponseSchema = ApplyJobSchema.nullable();
export type GetActiveApplyJobResponse = z.infer<typeof GetActiveApplyJobResponseSchema>;

/**
 * GET /v1/classify/apply-jobs/:id - returns full job details
 */
export const GetApplyJobResponseSchema = ApplyJobSchema;
export type GetApplyJobResponse = z.infer<typeof GetApplyJobResponseSchema>;
